// ImpactFx — burst of particles fired at the centre of the arena on
// crit / combo / ultimate / dodge events. Purely visual, no physics.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const BURSTS = {
  crit:     { count: 40, color: '#ffcf5c', size: 0.09, lifespan: 0.55, speed: 6 },
  combo:    { count: 32, color: '#e879ff', size: 0.08, lifespan: 0.5,  speed: 5 },
  ultimate: { count: 60, color: '#ffe28a', size: 0.11, lifespan: 0.9,  speed: 7.5 },
  dodge:    { count: 20, color: '#b7f3ff', size: 0.05, lifespan: 0.4,  speed: 3.5 },
  counter:  { count: 26, color: '#ff7c5c', size: 0.08, lifespan: 0.55, speed: 5.5 },
  attack:   { count: 14, color: '#cfe0d0', size: 0.06, lifespan: 0.32, speed: 3.5 },
};

export default function ImpactFx({ kind, cursor }) {
  const [bursts, setBursts] = useState([]);
  const prevCursor = useRef(cursor);

  useEffect(() => {
    if (cursor !== prevCursor.current) {
      prevCursor.current = cursor;
      const preset = BURSTS[kind];
      if (!preset) return;
      const id = `${cursor}-${Math.random().toString(36).slice(2, 6)}`;
      setBursts((prev) => [...prev.slice(-3), { id, kind, born: performance.now() }]);
    }
  }, [cursor, kind]);

  // Cull expired
  useFrame(() => {
    const now = performance.now();
    setBursts(prev => prev.filter(b => (now - b.born) < (BURSTS[b.kind]?.lifespan || 0.6) * 1000));
  });

  return bursts.map(b => <Burst key={b.id} kind={b.kind} born={b.born} />);
}


function Burst({ kind, born }) {
  const preset = BURSTS[kind] || BURSTS.attack;
  const mesh = useRef();
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const seeds = useMemo(() =>
    Array.from({ length: preset.count }, (_, i) => {
      const a = Math.random() * Math.PI * 2;
      const p = Math.random() * Math.PI;
      return {
        dir: [Math.sin(p) * Math.cos(a), Math.cos(p), Math.sin(p) * Math.sin(a)],
        speed: preset.speed * (0.6 + Math.random() * 0.7),
      };
    }), [preset.count, preset.speed]);

  useFrame(() => {
    if (!mesh.current) return;
    const t = (performance.now() - born) / (preset.lifespan * 1000);
    const decay = Math.max(0, 1 - t);
    for (let i = 0; i < seeds.length; i++) {
      const s = seeds[i];
      const d = s.speed * t;
      dummy.position.set(s.dir[0] * d, 1.0 + s.dir[1] * d * 0.8, s.dir[2] * d);
      dummy.scale.setScalar(decay);
      dummy.updateMatrix();
      mesh.current.setMatrixAt(i, dummy.matrix);
    }
    mesh.current.instanceMatrix.needsUpdate = true;
    if (mesh.current.material) mesh.current.material.opacity = 0.9 * decay;
  });

  return (
    <instancedMesh ref={mesh} args={[undefined, undefined, seeds.length]}>
      <sphereGeometry args={[preset.size, 6, 6]} />
      <meshBasicMaterial color={preset.color} transparent opacity={0.9} />
    </instancedMesh>
  );
}
